import { Badge, Box, Flex, type BoxProps } from "@chakra-ui/react";
import { type ReactNode } from "react";

interface SelectableCardProps extends Omit<BoxProps, "children"> {
  isSelected: boolean;
  isDisabled?: boolean;
  disabledLabel?: string;
  // Rendered as a <button>, so default to "button" to avoid submitting a surrounding form on select.
  type?: "button" | "submit" | "reset";
  children: ReactNode;
}

/** What the card does when it is pointed at or focused — one edge, lifted slightly. */
const RAISED = { borderColor: "smPrimarySoft", shadow: "sm" } as const;

export const SelectableCard = ({
  isSelected,
  isDisabled = false,
  disabledLabel,
  type = "button",
  as = "button",
  children,
  ...boxProps
}: SelectableCardProps) => {
  // RadioOptionGroup renders this as a <label> wrapping a radio. `type` and `disabled` mean nothing
  // there and are not valid on it, so they only go on when the card really is a button.
  const isButton = as === "button";
  return (
  <Box
    as={as}
    type={isButton ? type : undefined}
    borderWidth="2px"
    borderRadius="lg"
    borderColor={isSelected ? "smPrimarySoft" : "smBorder"}
    bg={isDisabled ? "smBackgroundSecondary" : isSelected ? "smPrimarySofter" : "smBackground"}
    p="4"
    textAlign="left"
    transition="all 0.15s ease"
    opacity={isDisabled ? 0.6 : 1}
    cursor={isDisabled ? "not-allowed" : "pointer"}
    disabled={isButton && isDisabled ? true : undefined}
    aria-disabled={isDisabled || undefined}
    _hover={isDisabled ? {} : RAISED}
    // The same treatment as hover, written once: a ring outside the card's own 2px edge reads as a
    // second edge, and on a selected card as two teal rings at once.
    _focusVisible={isDisabled ? {} : { ...RAISED, outline: "none" }}
    {...boxProps}
  >
    {isDisabled && disabledLabel ? (
      // The badge shares the row rather than floating over it: a reserved padding only fits the
      // label it was measured against, and the longer ones then run across the title.
      <Flex gap="3" align="flex-start" justify="space-between">
        <Box minW="0">{children}</Box>
        <Badge colorScheme="gray" fontSize="2xs" variant="subtle" whiteSpace="normal" textAlign="right">
          {disabledLabel}
        </Badge>
      </Flex>
    ) : (
      children
    )}
  </Box>
  );
};
